import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import DismissibleIntro from "@/components/DismissibleIntro";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import Cookies from "js-cookie";
import { shareImage } from "@/lib/share";
import { rememberBack } from "@/utils/propertyBackTracker";
import {
  FilmSlate, DownloadSimple, Trash, House, ImageSquare,
  FilePdf, FileVideo, ShareNetwork, Spinner, Archive, PencilSimple,
} from "@phosphor-icons/react";

/**
 * /dashboard/contenuti — Archivio dei post / reel / story / brochure generati
 * dal Social Studio. Backend: routes/social_assets.py.
 *
 * P2 (iter 246): risolve "ho generato 10 caroselli e li ho persi, sono solo
 * nei miei Downloads". Adesso ogni volta che scarichi qualcosa dal Social
 * Studio, parallelamente lo salviamo qui in archivio e l'utente può
 * ri-scaricarlo o ri-condividerlo a piacere.
 */

const KIND_LABEL = {
  post: "Post quadrato",
  carousel: "Carosello",
  reel: "Reel",
  story: "Story",
  brochure: "PDF stampabile",
  volantino: "Volantino A5",
  other: "Contenuto",
};

const KIND_ICON = {
  post: ImageSquare,
  carousel: ImageSquare,
  reel: FileVideo,
  story: ImageSquare,
  brochure: FilePdf,
  volantino: FilePdf,
  other: FilmSlate,
};

const FORMAT_COLOR = {
  pdf: "bg-red-100 text-red-700",
  png: "bg-emerald-100 text-emerald-700",
  jpg: "bg-emerald-100 text-emerald-700",
  jpeg: "bg-emerald-100 text-emerald-700",
  zip: "bg-amber-100 text-amber-700",
  mp4: "bg-violet-100 text-violet-700",
  mov: "bg-violet-100 text-violet-700",
};

function fileUrlFor(assetId, { dl = false } = {}) {
  const token = Cookies.get("vista_jwt");
  const qs = new URLSearchParams();
  if (token) qs.append("auth", token);
  if (dl) qs.append("dl", "1");
  return `${API}/social-assets/${assetId}/file?${qs.toString()}`;
}

function formatBytes(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return "—"; }
}

export default function MyContent() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterKind, setFilterKind] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterKind ? `?kind=${filterKind}&limit=100` : "?limit=100";
      const { data } = await api.get(`/social-assets${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, [filterKind]);

  useEffect(() => { load(); }, [load]);

  const onDownload = async (asset) => {
    const url = fileUrlFor(asset.id, { dl: true });
    try {
      await shareImage({
        url,
        filename: asset.original_filename || `pronto-${asset.kind}.${asset.format}`,
        title: KIND_LABEL[asset.kind] || "Contenuto Pronto",
        text: "",
      });
    } catch (e) {
      toast.error("Download non riuscito, riprova");
    }
  };

  const onDelete = async (asset) => {
    if (!window.confirm("Eliminare questo contenuto dall'archivio? (il file nel tuo telefono non viene toccato)")) return;
    try {
      await api.delete(`/social-assets/${asset.id}`);
      setItems((prev) => prev.filter((x) => x.id !== asset.id));
      setTotal((t) => Math.max(0, t - 1));
      toast.success("Eliminato");
    } catch (e) {
      toast.error("Eliminazione fallita");
    }
  };

  const kindOptions = [
    { v: null, l: "Tutti" },
    { v: "carousel", l: "Carosello" },
    { v: "post", l: "Post" },
    { v: "story", l: "Story" },
    { v: "reel", l: "Reel" },
  ];

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="my-content-page">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E] flex items-center gap-2">
              <Archive size={14} weight="fill" /> Archivio contenuti
            </div>
            <h1 className="font-editorial text-3xl sm:text-4xl mt-1">I miei contenuti</h1>
            <p className="text-sm text-[#6B6A65] mt-2 max-w-xl leading-relaxed">
              Caroselli, post, reel e brochure che hai generato dall'Editor social.
              Ri-scarica o ri-condividi in qualsiasi momento — qui li trovi sempre,
              anche dopo aver svuotato i Downloads del telefono.
            </p>
          </div>
        </div>

        {/* iter 275: banner "come si popola" sempre visibile (non collapsable).
            L'utente non capiva da dove arrivassero i contenuti — pensava di
            doverli caricare manualmente. */}
        <div className="mb-5 p-3.5 rounded-lg bg-[#F1F4ED] border border-[#7C8B6E]/20 flex items-start gap-3" data-testid="my-content-howto">
          <div className="w-8 h-8 rounded-full bg-white border border-[#7C8B6E]/30 flex items-center justify-center flex-shrink-0">
            <DownloadSimple size={15} weight="bold" className="text-[#7C8B6E]" />
          </div>
          <div className="text-[12.5px] text-[#1C1C1A] leading-relaxed">
            <strong className="font-semibold">Come funziona</strong> · Ogni volta che <strong>scarichi</strong> un post, una story, un reel o una brochure dal{" "}
            <Link to="/dashboard/social" className="text-[#7C8B6E] underline font-semibold">Editor social</Link>,
            lo salviamo qui in automatico. Non devi caricare nulla a mano. Da qui puoi
            ri-scaricarlo, mandarlo su WhatsApp o aprirlo per modificarlo.
          </div>
        </div>

        {/* iter 281: ricorda dove finiscono i file scaricati. Naming
            convention nuova → l'agente li ritrova per nome immobile. */}
        <div className="mb-5 p-3 rounded-lg bg-[#FAFAF7] border border-[#EAE7DF] flex items-start gap-3" data-testid="my-content-naming-tip">
          <span className="text-[18px] leading-none mt-0.5" aria-hidden>💾</span>
          <div className="text-[11.5px] text-[#6B6A65] leading-relaxed">
            <strong className="text-[#1C1C1A] font-semibold">Dove sono i file scaricati?</strong>{" "}
            Cartella <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-[#EAE7DF] text-[10.5px]">Download</span> del dispositivo,
            tutti con prefisso <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-[#EAE7DF] text-[10.5px]">pronto-{`{nome-immobile}`}-…</span>.
            Esempio: <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-[#EAE7DF] text-[10.5px]">pronto-trilocale-brera-nuovo-annuncio-elegante.png</span>.
            Cerca il nome dell'immobile nella barra di ricerca file e li trovi tutti insieme.
          </div>
        </div>

        <div className="flex items-center gap-2 mb-5 overflow-x-auto hide-scroll" data-testid="content-filter">
          {kindOptions.map((o) => (
            <button
              key={String(o.v)}
              onClick={() => setFilterKind(o.v)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                filterKind === o.v
                  ? "bg-[#1C1C1A] text-white"
                  : "bg-[#F1F4ED] text-[#1C1C1A] hover:bg-[#E5E7E0]"
              }`}
              data-testid={`filter-${o.v ?? "all"}`}
            >
              {o.l}
            </button>
          ))}
          {!loading && (
            <span className="ml-auto text-xs text-[#9A968B] whitespace-nowrap" data-testid="content-total">
              {total} {total === 1 ? "contenuto" : "contenuti"}
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#6B6A65]">
            <Spinner size={20} className="animate-spin mr-2" /> Carico…
          </div>
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {items.map((a) => (
              <AssetCard
                key={a.id}
                asset={a}
                onDownload={() => onDownload(a)}
                onDelete={() => onDelete(a)}
              />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function AssetCard({ asset, onDownload, onDelete }) {
  const Icon = KIND_ICON[asset.kind] || FilmSlate;
  const fmtClass = FORMAT_COLOR[asset.format] || "bg-[#F1F4ED] text-[#7C8B6E]";
  const isImage = ["png", "jpg", "jpeg", "webp"].includes(asset.format);
  return (
    <div
      className="bg-white border border-[#E5E7E0] rounded-xl overflow-hidden flex flex-col"
      data-testid={`content-card-${asset.id}`}
    >
      <div className="aspect-square bg-[#F1EEE6] flex items-center justify-center relative overflow-hidden">
        {isImage ? (
          <img
            src={fileUrlFor(asset.id)}
            alt={KIND_LABEL[asset.kind] || "Contenuto"}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <Icon size={42} weight="light" className="text-[#C8C4B7]" />
        )}
        <span className={`absolute top-2 left-2 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${fmtClass}`}>
          {asset.format}
        </span>
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E]">
          {KIND_LABEL[asset.kind] || "Contenuto"}
        </div>
        <div className="text-[13px] font-semibold text-[#1C1C1A] mt-0.5 truncate">
          {asset.label || asset.property_title || "—"}
        </div>
        {asset.property_id && (
          <Link
            to={`/dashboard/property/${asset.property_id}`}
            onClick={() => rememberBack(asset.property_id, "I miei contenuti", "/dashboard/contenuti")}
            className="text-[11px] text-[#7C8B6E] hover:underline truncate flex items-center gap-1 mt-0.5"
            data-testid="card-property-link"
          >
            <House size={10} /> {asset.property_title || "Apri immobile"}
          </Link>
        )}
        <div className="text-[10px] text-[#9A968B] mt-1">
          {formatDate(asset.created_at)} · {formatBytes(asset.size_bytes)}
        </div>

        <div className="flex items-center gap-1.5 mt-3 -mb-1">
          {/* iter 275: bottone Modifica → apre Social Studio per ri-generare
              quel contenuto con stile diverso. Visibile solo se l'asset è
              ancora collegato a un immobile esistente. */}
          {asset.property_id && (
            <Link
              to={`/dashboard/property/${asset.property_id}?tab=social`}
              onClick={() => rememberBack(asset.property_id, "I miei contenuti", "/dashboard/contenuti")}
              className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11.5px] font-semibold bg-white border border-[#1C1C1A] text-[#1C1C1A] py-1.5 rounded-full hover:bg-[#F1F4ED]"
              data-testid="card-edit"
              title="Modifica nell'Editor social"
            >
              <PencilSimple size={12} weight="bold" /> Modifica
            </Link>
          )}
          <button
            onClick={onDownload}
            className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11.5px] font-semibold bg-[#1C1C1A] text-white py-1.5 rounded-full hover:bg-black"
            data-testid="card-download"
          >
            <DownloadSimple size={12} weight="bold" /> Scarica
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-[#9A968B] hover:text-red-600 transition-colors"
            title="Elimina"
            data-testid="card-delete"
          >
            <Trash size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-16 px-4" data-testid="content-empty">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F1F4ED] mb-4">
        <Archive size={26} weight="light" className="text-[#7C8B6E]" />
      </div>
      <div className="font-editorial text-2xl mb-2">Ancora niente in archivio</div>
      <p className="text-sm text-[#6B6A65] max-w-md mx-auto leading-relaxed mb-5">
        Genera il tuo primo carosello, post o reel dall&apos;Editor social.
        Ogni volta che scarichi un contenuto, lo salveremo qui per te.
      </p>
      <Link
        to="/dashboard/social"
        className="inline-flex items-center gap-2 bg-[#1C1C1A] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-black"
        data-testid="empty-go-social"
      >
        <FilmSlate size={14} weight="fill" /> Vai all&apos;Editor social
      </Link>
    </div>
  );
}
